import { compareDesc, format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import pluralize from 'pluralize';
import { EmptyMessage } from '@/components/atoms/EmptyMessage';
import { cn } from '@/lib/classNames';
import type {
	Feed,
	ReportCategoryResult,
	ResultReport
} from '../../@types/types';
import { useReportData } from '../../hooks/useReportData';

export const ActivityFeed = () => {
	const { report } = useReportData();

	const feedReport = useMemo(() => {
		if (!report.result) return [];

		const sortedResult = Object.entries(report.result).sort(
			([date1], [date2]) => compareDesc(new Date(date1), new Date(date2))
		);

		return sortedResult.map<Feed>(([key, value]) => {
			const activities = Object.entries(value as ResultReport)
				.map(([k, v]) => {
					if ((v as ReportCategoryResult).contacted) {
						const { contacted } = v as ReportCategoryResult;

						return `Contacted ${pluralize(
							'business',
							contacted,
							true
						)} - ${k}.`;
					}

					if (k === 'businessesExported') {
						return `Exported ${pluralize(
							'business',
							Number(v),
							true
						)}.`;
					}

					if (k === 'listsCreated') {
						const numLists = report?.lists?.length;

						return `Created ${pluralize('list', numLists, true)}.`;
					}

					return undefined;
				})
				.filter(Boolean);

			return { date: parseISO(key), activities } as Feed;
		});
	}, [report?.lists?.length, report.result]);

	return (
		<div
			data-has-feed={feedReport.length > 0}
			className="relative flex h-full min-h-[60vh] w-full flex-col gap-6 overflow-auto data-[has-feed=false]:flex-1 data-[has-feed=false]:items-center data-[has-feed=false]:justify-center md:min-h-0"
		>
			<section className="absolute left-0 top-0 flex h-full w-full flex-col gap-3 overflow-auto">
				{feedReport.length > 0 ? (
					feedReport.map(feed => (
						<section
							key={
								feed.date.toISOString() +
								feed.activities.join('')
							}
							className="flex flex-col gap-3"
						>
							<header className="relative flex items-center gap-3">
								<span className="absolute left-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-orange" />
								<p className="ml-4 font-barlow text-lightest-grey">
									{format(feed.date, 'do MMMM')}
								</p>
							</header>

							<ul
								className={cn([
									'relative max-h-96 overflow-auto pl-4',
									"before:absolute before:left-1 before:top-0 before:h-full before:w-0.5 before:-translate-x-px before:bg-dark before:content-['']"
								])}
							>
								{feed.activities.map(activity => (
									<li
										key={activity + feed.date.toISOString()}
										className="text-lighter-grey"
									>
										{activity}
									</li>
								))}
							</ul>
						</section>
					))
				) : (
					<EmptyMessage
						title="No activity to report yet!"
						message="Your daily interactions with Resquared will be made viewable here."
						className="relative h-full"
					/>
				)}
			</section>
		</div>
	);
};
